import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, Briefcase, HeartHandshake, Sprout } from "lucide-react";
import { COMPANY, TEXTURE_BG, CULTURE_PHOTOS, LEADERSHIP } from "../lib/data";

const About = () => {
    return (
        <main data-testid="about-page" className="bg-background">
            {/* HEADER */}
            <section className="relative px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-16 border-b border-border overflow-hidden">
                <img src={TEXTURE_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />
                <div className="relative">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                        People & Culture
                    </div>
                    <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                        The people
                        <br />
                        <span className="text-accent">behind the build.</span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
                        Mark Wilson Construction is more than the structures we build. It's the
                        relationships, the mentorship, and the culture we build alongside them.
                    </p>
                </div>
            </section>

            {/* STORY */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            01 / Story
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                            Since {COMPANY.founded}.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-6 text-lg text-muted-foreground leading-relaxed">
                        <p>
                            Since 1997, Mark Wilson Construction has set the standard for
                            responsible commercial building in the San Joaquin Valley. Almost three
                            decades later, our team still operates with the same intent: do the
                            right thing, for the right reasons, with the right people.
                        </p>
                        <p>
                            We're a team of builders, creating more than buildings. We build
                            lasting relationships, earn trust, and invest in the next generation
                            of craftspeople. Every project reflects the people, teamwork, and
                            dedication behind it.
                        </p>
                    </div>
                </div>
            </section>

            {/* LEADERSHIP */}
            <section
                data-testid="leadership-section"
                className="relative bg-secondary text-secondary-foreground overflow-hidden"
            >
                <img
                    src={TEXTURE_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none"
                />
                <div className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                        01.5 / Leadership
                    </div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mb-16">
                        Who's behind
                        <br />
                        <span className="text-accent">the build.</span>
                    </h2>

                    <div className="flex items-center gap-4 mb-10">
                        <div className="h-px w-10 bg-accent" />
                        <h3 className="font-mono text-sm tracking-[0.35em] uppercase text-secondary-foreground">
                            Executive Team
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
                        {LEADERSHIP.map((person) => (
                            <div
                                key={person.email}
                                data-testid={`leader-${person.name.split(" ")[0].toLowerCase()}`}
                                className="flex flex-col items-center text-center"
                            >
                                <div className="w-44 h-44 md:w-48 md:h-48 rounded-full overflow-hidden bg-foreground/40 ring-1 ring-secondary-foreground/10">
                                    <img
                                        src={person.photo}
                                        alt={`${person.name} — ${person.title}`}
                                        className="w-full h-full object-cover grayscale"
                                    />
                                </div>
                                <div className="mt-6 font-display font-black text-xl uppercase tracking-tight">
                                    {person.name}
                                </div>
                                <div className="mt-1 font-mono text-[11px] tracking-[0.25em] uppercase text-accent">
                                    {person.title}
                                </div>
                                <a
                                    href={`mailto:${person.email}`}
                                    data-testid={`leader-email-${person.name.split(" ")[0].toLowerCase()}`}
                                    className="mt-4 text-xs text-secondary-foreground/70 hover:text-accent transition-colors break-all"
                                >
                                    {person.email}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section data-testid="culture-strip" className="bg-secondary text-secondary-foreground">
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                        02 / In the Field
                    </div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mb-12">
                        How we work.
                        <br /> Together.
                    </h2>

                    <div className="columns-1 sm:columns-2 gap-3 [column-fill:_balance]">
                        {CULTURE_PHOTOS.map((src, i) => (
                            <div
                                key={i}
                                className="mb-2 overflow-hidden bg-secondary break-inside-avoid"
                            >
                                <img
                                    src={src}
                                    alt="Team and culture moment"
                                    className="block w-full h-auto"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THREE C's — removed (already shown on Home) */}

            {/* PHILOSOPHY / QUOTE */}
            <section className="bg-foreground text-background">
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                        04 / Philosophy
                    </div>
                    <h2 className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tight leading-[0.9] max-w-3xl">
                        Be a builder
                        <br /> in everything you do.
                    </h2>

                    <div className="mt-16 grid md:grid-cols-3 gap-px bg-background/15 border border-background/15">
                        {[
                            { tag: "LISTEN", body: "We start by understanding the project, the people, and the priorities." },
                            { tag: "PLAN", body: "Through communication and collaboration, we plan together — before we build." },
                            { tag: "BUILD", body: "We execute with safety, quality, schedule, and cost as constants." },
                        ].map((item) => (
                            <div key={item.tag} className="bg-foreground p-8 md:p-10">
                                <div className="font-display font-black text-5xl text-accent">
                                    {item.tag}
                                </div>
                                <p className="mt-4 text-background/80 leading-relaxed">
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20 border-l-2 border-accent pl-6 max-w-3xl">
                        <Quote className="w-8 h-8 text-accent mb-4" />
                        <blockquote className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
                            "At the end of the day people won't remember what you said or did, they
                            will remember how you made them feel."
                        </blockquote>
                        <div className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-background/60">
                            — Maya Angelou
                        </div>
                    </div>
                </div>
            </section>

            {/* JOIN OUR TEAM */}
            <section data-testid="careers-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            05 / Careers
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                            Join our team.
                        </h2>
                        <p className="mt-6 text-lg text-muted-foreground max-w-md">
                            We're always looking for builders — superintendents, project engineers,
                            estimators, interns, and craftspeople who want to grow with us.
                        </p>
                        <a
                            href="mailto:humanresources@markwilsonconstruction.com?subject=Career%20Interest%20-%20Mark%20Wilson%20Construction"
                            data-testid="careers-cta"
                            className="mt-8 inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-4 font-bold uppercase tracking-wider hover:bg-foreground transition-colors"
                        >
                            Send your resume
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>

                    <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-background border border-border">
                        {[
                            { icon: <Sprout className="w-6 h-6" />, k: "Mentorship", v: "Learn from senior builders who invest in your career." },
                            { icon: <Briefcase className="w-6 h-6" />, k: "Real Projects", v: "Day one ownership on jobs that shape the Valley." },
                            { icon: <HeartHandshake className="w-6 h-6" />, k: "Culture", v: "A team that operates on trust, communication, and respect." },
                            { icon: <Quote className="w-6 h-6" />, k: "Purpose", v: "Schools, clinics, churches — work that outlives the contract." },
                        ].map((item) => (
                            <div key={item.k} className="bg-card p-6 md:p-8 flex items-start gap-4">
                                <div className="text-accent mt-1">{item.icon}</div>
                                <div>
                                    <div className="font-display font-bold text-lg uppercase tracking-tight">{item.k}</div>
                                    <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.v}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-20 border-t border-border pt-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight max-w-md">
                        Want to build with us? Start here.
                    </div>
                    <Link
                        to="/contact"
                        data-testid="about-cta"
                        className="group inline-flex items-center gap-2 bg-foreground text-background px-6 py-4 font-bold uppercase tracking-wider hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                        Contact our team
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </main>
    );
};

export default About;
